/**
 * atomberg-warranty-scan.js
 *
 * Barcode/serial-number camera scanning for Register Warranty's repeatable
 * product rows. Uses the native
 * BarcodeDetector API where the platform ships one (Chrome on
 * Android/ChromeOS/macOS); everywhere else (Windows/Linux Chrome, all iOS
 * browsers, Firefox) it lazy-loads a vendored zxing-wasm decoder
 * (atomberg-zxing-reader.js + base64-embedded wasm in
 * atomberg-zxing-reader-wasm.js, ~1.4MB, fetched ONLY on the first scan
 * click) and decodes video frames via canvas. Manual serial entry always
 * works everywhere and is unaffected.
 *
 * Drives the shared #atomberg-warranty-scan-sheet (atomberg-sheet, variant
 * center) rendered once by atomberg-warranty-scan-modal.liquid, which also
 * carries the decoder asset URLs as data attributes.
 */
(function () {
  'use strict';

  var w = /** @type {any} */ (window);
  if (w._atombergWarrantyScanInit) return;
  w._atombergWarrantyScanInit = true;

  var MODAL_ID = 'atomberg-warranty-scan-sheet';
  var NATIVE = 'BarcodeDetector' in window;
  var CAN_FALLBACK =
    'WebAssembly' in window && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  var SUPPORTED = NATIVE || CAN_FALLBACK;
  var BARCODE_FORMATS = ['code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'qr_code'];
  // Same formats in zxing-wasm's naming for the fallback decoder.
  var ZXING_FORMATS = ['Code128', 'Code39', 'EAN-13', 'EAN-8', 'UPC-A', 'UPC-E', 'QRCode'];

  /** @type {HTMLInputElement|null} the serial input currently being scanned into */
  var activeInput = null;
  /** @type {MediaStream|null} */
  var activeStream = null;
  /** @type {number|null} */
  var rafId = null;
  /** @type {number} set when the scan sheet opens; 0 when no scan is in progress */
  var scanStartedAt = 0;

  /** @returns {string} */
  function detectionMethod() {
    return NATIVE ? 'native' : 'fallback';
  }

  /** @returns {string} */
  function deviceType() {
    return /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
  }

  /**
   * 1-indexed position of input's row among its repeatable siblings, and the
   * total row count — needed on every Warranty tracking-plan event fired
   * per product row (Product Position / Total Products in Form), since a
   * registration can carry multiple products under one invoice.
   * @param {HTMLInputElement} input
   * @returns {{position: number, total: number}}
   */
  function rowContext(input) {
    var row = input.closest('[data-support-repeatable-row]');
    var container = input.closest('[data-support-repeatable]');
    if (!row || !container) return { position: 1, total: 1 };
    var rows = Array.from(container.querySelectorAll('[data-support-repeatable-row]'));
    var index = rows.indexOf(row);
    return { position: index === -1 ? 1 : index + 1, total: rows.length };
  }

  function stopScan() {
    if (rafId !== null) {
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }
    if (activeStream) {
      activeStream.getTracks().forEach(function (track) {
        track.stop();
      });
      activeStream = null;
    }
    activeInput = null;
    scanStartedAt = 0;
  }

  /**
   * @param {HTMLElement} modal
   * @param {string} message
   */
  function setError(modal, message) {
    var el = /** @type {HTMLElement|null} */ (
      modal.querySelector('[data-support-warranty-scan-error]')
    );
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  /**
   * @param {string} src
   * @returns {Promise<void>}
   */
  function injectScript(src) {
    return new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = function () {
        resolve(undefined);
      };
      script.onerror = function () {
        reject(new Error('Failed to load ' + src));
      };
      document.head.appendChild(script);
    });
  }

  /** @type {Promise<any>|null} */
  var zxingReady = null;

  /**
   * Loads the vendored zxing-wasm decoder on first use. The wasm binary is
   * base64-embedded in a plain JS asset instead of a .wasm file so it serves
   * from the theme CDN with no content-type/extension concerns.
   * @returns {Promise<any>} the ZXingWASM global, module pre-compiled
   */
  function loadFallbackDecoder() {
    if (zxingReady) return zxingReady;
    var root = document.querySelector('[data-support-scan-decoder-src]');
    var decoderSrc = root ? root.getAttribute('data-support-scan-decoder-src') : '';
    var wasmSrc = root ? root.getAttribute('data-support-scan-wasm-src') : '';
    if (!decoderSrc || !wasmSrc) {
      return Promise.reject(new Error('Decoder asset URLs missing'));
    }
    zxingReady = Promise.all([injectScript(decoderSrc), injectScript(wasmSrc)])
      .then(function () {
        var zxing = w.ZXingWASM;
        var b64 = w.AtombergZXingWasmB64;
        if (!zxing || !b64) throw new Error('Decoder failed to initialize');
        var binary = window.atob(b64);
        var bytes = new Uint8Array(binary.length);
        for (var i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        // fireImmediately compiles the wasm now, while the camera permission
        // prompt/stream is still settling, instead of on the first frame.
        return Promise.resolve(
          zxing.prepareZXingModule({
            overrides: { wasmBinary: bytes.buffer },
            fireImmediately: true,
          })
        ).then(function () {
          return zxing;
        });
      })
      .catch(function (err) {
        zxingReady = null; // allow a retry on the next scan click
        throw err;
      });
    return zxingReady;
  }

  /**
   * Resolves to a detect(video) → Promise<{value, format}> frame reader
   * (value: '' = no barcode in frame), backed by native BarcodeDetector when
   * present, else the lazy-loaded zxing-wasm fallback reading frames via
   * canvas. format is used only for the scan_succeeded analytics event.
   * @returns {Promise<function(HTMLVideoElement): Promise<{value: string, format: string}>>}
   */
  function createFrameDetector() {
    if (NATIVE) {
      var Detector = /** @type {any} */ (window).BarcodeDetector;
      var detector = new Detector({ formats: BARCODE_FORMATS });
      return Promise.resolve(function (/** @type {HTMLVideoElement} */ video) {
        return detector.detect(video).then(function (/** @type {any[]} */ codes) {
          var first = codes && codes[0];
          return first
            ? { value: String(first.rawValue), format: String(first.format || '') }
            : { value: '', format: '' };
        });
      });
    }
    return loadFallbackDecoder().then(function (zxing) {
      var canvas = document.createElement('canvas');
      var ctx = canvas.getContext('2d', { willReadFrequently: true });
      return function (/** @type {HTMLVideoElement} */ video) {
        if (!ctx || !video.videoWidth) return Promise.resolve({ value: '', format: '' });
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0);
        var imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        return zxing
          .readBarcodes(imageData, { formats: ZXING_FORMATS, maxNumberOfSymbols: 1 })
          .then(function (/** @type {Array<{text: string, format?: string}>} */ results) {
            var first = results && results[0];
            return first
              ? { value: String(first.text), format: String(first.format || '') }
              : { value: '', format: '' };
          });
      };
    });
  }

  /**
   * Some products carry a plain serial barcode; others carry a QR encoding
   * the old site's registration URL (…?gettab=register-warranty&
   * bg_serialnumber=…). Return the bare serial either way: URLs yield their
   * serial param (same three names the wizard's deep link accepts), and a
   * URL with NO serial param yields '' so the scan loop keeps looking
   * instead of dumping a URL into the serial field.
   * @param {string} value
   * @returns {string}
   */
  function extractSerial(value) {
    var raw = String(value || '').trim();
    if (!/^https?:\/\//i.test(raw)) return raw;
    try {
      var params = new URL(raw).searchParams;
      return (
        params.get('bg_serialnumber') ||
        params.get('serialnumber') ||
        params.get('serial') ||
        ''
      ).trim();
    } catch (_) {
      return '';
    }
  }

  /** @param {HTMLInputElement} input */
  function startScan(input) {
    var modalEl = document.getElementById(MODAL_ID);
    if (!modalEl) return;
    var modal = /** @type {HTMLElement} */ (modalEl);
    var videoEl = /** @type {HTMLVideoElement|null} */ (
      modal.querySelector('[data-support-warranty-scan-video]')
    );
    if (!videoEl) return;
    var video = videoEl;

    activeInput = input;
    setError(modal, '');
    w.AtombergSheet.open(MODAL_ID);

    scanStartedAt = Date.now();
    var openContext = rowContext(input);
    document.dispatchEvent(
      new CustomEvent('atomberg:warranty-scan-opened', {
        detail: {
          detection_method: detectionMethod(),
          device_type: deviceType(),
          product_position: openContext.position,
          total_products_in_form: openContext.total,
          warranty_variant: (w.AtombergWarrantyAnalytics || {}).variant || '',
        },
      })
    );

    var cameraReady = false;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then(function (stream) {
        activeStream = stream;
        video.srcObject = stream;
        return video.play();
      })
      .then(function () {
        cameraReady = true;
        return createFrameDetector();
      })
      .then(function (detectFrame) {
        function tick() {
          if (!activeStream) return;
          detectFrame(video)
            .then(function (result) {
              var serial = extractSerial(result.value);
              if (serial && activeInput) {
                var input = activeInput;
                var durationMs = scanStartedAt ? Date.now() - scanStartedAt : 0;
                var successContext = rowContext(input);
                // Read by atomberg-support-repeatable.js's serial blur handler
                // (entered_serial_number's Input Method), then cleared there —
                // a later manual edit of the same field must not still report
                // "Scanned". Set before dispatching input/blur below so it's
                // already in place when that handler runs.
                var scanRow = input.closest('[data-support-repeatable-row]');
                if (scanRow) scanRow.setAttribute('data-support-row-serial-source', 'scanned');
                input.value = serial;
                input.dispatchEvent(new Event('input', { bubbles: true }));
                input.dispatchEvent(new Event('blur', { bubbles: true }));
                document.dispatchEvent(
                  new CustomEvent('atomberg:warranty-scan-succeeded', {
                    detail: {
                      detection_method: detectionMethod(),
                      device_type: deviceType(),
                      barcode_format: result.format || '',
                      duration_ms: durationMs,
                      serial_number: serial,
                      product_position: successContext.position,
                      total_products_in_form: successContext.total,
                      warranty_variant: (w.AtombergWarrantyAnalytics || {}).variant || '',
                    },
                  })
                );
                stopScan();
                w.AtombergSheet.close(MODAL_ID);
                return;
              }
              rafId = window.requestAnimationFrame(tick);
            })
            .catch(function () {
              rafId = window.requestAnimationFrame(tick);
            });
        }
        rafId = window.requestAnimationFrame(tick);
      })
      .catch(function () {
        setError(
          modal,
          cameraReady
            ? 'Could not start the scanner on this browser. Enter the serial number manually.'
            : 'Camera access was denied. Enter the serial number manually.'
        );
        stopScan();
      });
  }

  document.addEventListener('click', function (e) {
    var target = /** @type {HTMLElement} */ (e.target);
    if (!target || !target.closest) return;

    var scanTrigger = target.closest('[data-support-warranty-scan-trigger]');
    if (scanTrigger) {
      var row = scanTrigger.closest('[data-support-repeatable-row]');
      var input = /** @type {HTMLInputElement|null} */ (
        row ? row.querySelector('[data-support-row-serial]') : null
      );
      if (input) startScan(input);
      return;
    }

    // atomberg-sheet.js has no close event to hook, and any of its own close
    // triggers (overlay click, header X) can dismiss our modal — so treat a
    // click on any of them, scoped to our own modal, as "stop the camera".
    if (target.closest('#' + MODAL_ID + ' [data-atomberg-sheet-close]')) {
      stopScan();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var modal = document.getElementById(MODAL_ID);
    if (modal && modal.hasAttribute('data-open')) stopScan();
  });

  function showScanButtons() {
    if (!SUPPORTED) return;
    document.querySelectorAll('[data-support-warranty-scan-trigger]').forEach(function (btn) {
      /** @type {HTMLElement} */ (btn).hidden = false;
    });
  }

  // Repeatable rows get cloned well after this script's initial run — a
  // MutationObserver keeps newly-added scan buttons in sync with feature
  // support instead of requiring row-creation code elsewhere to remember to
  // re-run this.
  function observeNewRows() {
    if (!SUPPORTED) return;
    var root = document.querySelector('.atomberg-warranty');
    if (!root) return;
    var observer = new MutationObserver(showScanButtons);
    observer.observe(root, { childList: true, subtree: true });
  }

  function initAll() {
    showScanButtons();
    observeNewRows();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
